<script setup lang="ts">
import { styleZoom } from "@/common/utils/style";

interface SummaryItemType {
  id: string;
  label: string;
  page: number;
}

interface SummaryPageType {
  id: string;
  style: any;
  items: SummaryItemType[];
}

const props = defineProps<{
  avatar: any;
  company: any;
  footer: any;
  header: any;
  data: SummaryPageType;
  print?: boolean;
  style: any;
  summary: any;
  zoom: number;
}>();
</script>

<template>
  <div
    class="page"
    :class="{
      print: props.print,
    }"
    :style="styleZoom(style, zoom)"
  >
    <template v-if="header?.visible">
      <div
        class="page-header"
        :class="{
          'no-image': !avatar,
        }"
        :style="
          styleZoom(
            {
              'font-size': '16px',
            },
            zoom,
          )
        "
      >
        <template v-if="company">
          <div class="page-header-company">
            <div class="page-header-image">
              <img :src="company.avatar?.base64" alt="Logo" />
            </div>
          </div>
        </template>

        <div class="page-header-content">
          <div class="page-header-title">
            {{ header.title }}
          </div>
          <div class="page-header-subtitle">
            {{ header.subtitle }}
          </div>
        </div>

        <template v-if="avatar">
          <div class="page-header-image">
            <img :src="avatar.base64" alt="Logo" />
          </div>
        </template>
      </div>
    </template>

    <div class="page-content">
      <div class="summary-title">Índice</div>
      <div
        class="summary-list"
        :style="
          styleZoom(
            {
              'font-size': summary.size,
              'font-family': summary.font_family,
            },
            zoom,
          )
        "
      >
        <template v-for="item in data.items" :key="item.id">
          <div class="summary-item">
            <div class="item-title">
              {{ item.label }}
            </div>
            <div class="item-dot"></div>
            <div class="item-page">
              {{ item.page + 1 }}
            </div>
          </div>
        </template>
      </div>
    </div>

    <template v-if="footer?.visible">
      <div
        class="page-footer"
        :style="
          styleZoom(
            {
              'font-size': '16px',
            },
            zoom,
          )
        "
      >
        <div class="page-footer-content">
          <div class="page-footer-title">
            {{ footer.title }}
          </div>
          <div class="page-footer-subtitle">
            {{ footer.subtitle }}
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.page {
  display: grid;
  grid-template-rows: auto 1fr auto;
  margin-inline: auto;
  margin-bottom: 20px;
  position: relative;
  transition: all 0.3s ease;

  &.has_header {
    grid-template-rows: auto 1fr;
  }

  &.has_footer {
    grid-template-rows: 1fr auto;
  }

  &.print {
    width: 210mm !important;
    height: 296.75mm !important;
    max-height: 296.75mm !important;
    overflow: hidden;
    border: 0px;
    font-size: 16px;
    margin-bottom: 0;
  }

  .page-header {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    border-bottom: 1px solid #ccc;
    padding-block-end: 0.25rem;
    margin-block-end: 0.5rem;

    &.no-image {
      grid-template-columns: 1fr auto;

      .page-header-content {
        align-items: flex-start;
      }
    }

    .page-header-content {
      display: flex;
      flex-direction: column;
      align-items: center;

      .page-header-title {
        font-size: 0.875em;
        font-weight: bold;
      }

      .page-header-subtitle {
        font-size: 0.75em;
        color: #666;
      }
    }

    .page-header-company {
      display: flex;
      align-items: center;
    }

    .page-header-image {
      width: auto;
      height: 45px;
      border-radius: 4px;
      overflow: hidden;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
  }

  .page-content {
    display: flex;
    flex-direction: column;

    .summary-title {
      width: 100%;
      height: 60px;
      font-size: 1em;
      font-weight: bold;
      text-align: center;
      text-transform: uppercase;
    }

    .summary-list {
      display: flex;
      flex-direction: column;
      padding-inline: 2em;
      gap: 0.75em;

      .summary-item {
        width: 100%;
        display: grid;
        grid-template-columns: auto 1fr auto;
        gap: 0.5em;

        .item-title {
          font-weight: normal;
        }

        .item-dot {
          width: 100%;
          height: 1px;
          border-bottom: 3px dotted #ccc;
          margin-block-start: auto;
        }

        .item-page {
          font-weight: bold;
        }
      }
    }
  }

  .page-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-top: 1px solid #ccc;
    padding-block-start: 0.25rem;

    .page-footer-content {
      display: flex;
      flex-direction: column;

      .page-footer-title {
        font-size: 0.875em;
        font-weight: bold;
      }

      .page-footer-subtitle {
        font-size: 0.875em;
        color: #666;
      }
    }

    .page-footer-page {
      color: #666;
      font-size: 0.875em;
      font-weight: bold;
    }
  }
}
</style>
